import { getMyProvisioningStatus } from "@elation/api-client/src/stitch-web";

import { IS_CHROME_EXTENSION } from "@src/env";
import { navigation } from "@src/shared/navigation";
import { is404Error } from "@src/shared/utils/http/errors";
import { captureException } from "@src/shared/utils/observability/sentry";

export type ResolvePostAuthDestinationOptions = {
  route?: string;
  operation?: string;
};

/** Default post-auth landing route when no explicit redirect is provided. */
export function getDefaultPostAuthSuccessDestination(): string {
  return IS_CHROME_EXTENSION ? navigation.patients() : navigation.dashboard();
}

/**
 * Resolves where to send the user after signup or sign-in based on Elation
 * account provisioning status.
 */
export async function resolvePostAuthDestination(
  successDestination: string,
  options?: ResolvePostAuthDestinationOptions,
): Promise<string> {
  try {
    const response = await getMyProvisioningStatus();
    const state = response.data.state;

    if (state === "pending" || state === "failed") {
      return navigation.signupCompletion();
    }

    return successDestination;
  } catch (error) {
    if (is404Error(error)) {
      return successDestination;
    }

    const route = options?.route ?? "auth";
    const operation = options?.operation ?? "resolvePostAuthDestination";
    let errorId: string | undefined;
    try {
      errorId = captureException(error, {
        tags: { route, operation },
      });
    } catch (sentryError) {
      console.error(
        `[${route}] failed to report provisioning error to Sentry`,
        sentryError,
      );
    }
    console.error(
      `[${route}] unexpected error fetching provisioning status; routing to signup-completion`,
      { error, errorId },
    );
    return navigation.signupCompletion();
  }
}
